import React, { useEffect, useState, FunctionComponent, Children } from 'react';
import styles from '../../../../styles/customSwipeable.scss';
import ArrowSmallIcon from '../../../icons/arrowSmallIcon';
import classNames from 'classnames';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperCore } from 'swiper';
import { Mousewheel, Navigation, Grid } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/grid';

export interface ICustomSwiper {
    type: string;
    selectedElementIndex: number;
    // If sliderPreColumn are more than 1(default), columnPreView have to have some value but not 'auto'(default)
    direction?: 'horizontal' | 'vertical';
    columnsPerView?: number;
    slidesPerColumn?: number;
    spaceBetween?: number;
    mousewheel?: boolean;
    enabled: boolean;
    isWWS?: boolean;
    swipingEnabled?: boolean;
    startSwipeCallBack?: (swiper: SwiperCore) => void;
    endSwipeCallBack?: (swiper: SwiperCore) => void;
    setTabNavFocusable?: (el: Element, order?: number) => void;
}

const useSwiperRef = (): [HTMLDivElement | null, React.RefObject<HTMLDivElement>] => {
    const [wrapper, setWrapper] = useState<HTMLDivElement | null>(null);
    const ref = React.useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (ref.current)
            setWrapper(ref.current);
    }, []);

    return [wrapper, ref];
};

export const CustomSwiper: FunctionComponent<ICustomSwiper> = (props: React.PropsWithChildren<ICustomSwiper>) => {
    const [prevEl, leftArrowRef] = useSwiperRef();
    const [nextEl, rightArrowRef] = useSwiperRef();
    const direction = props.direction ?? 'horizontal';
    const slidersPerView = props.columnsPerView ?? 'auto';
    const slidersPerColumn = props.slidesPerColumn ?? 1;
    const spaceBetween = props.spaceBetween ?? 0;
    const childrenArray = Children.toArray(props.children);
    const swipingEnabled = props.swipingEnabled ?? true;

    useEffect(() =>  {
        if (props.setTabNavFocusable) {
            if (prevEl) {
                props.setTabNavFocusable(prevEl);
            }
            if (nextEl) {
                props.setTabNavFocusable(nextEl);
            }
        }
    }, [prevEl, nextEl]);

    return (
        <div className="scope-order-modal-swiper">
            <div
                className={classNames(styles.left_arrow, {
                    [styles.hidden]: !props.enabled,
                    [styles.vertical]: direction === 'vertical',
                    [styles.wws]: props.isWWS,
                })}
                ref={leftArrowRef}
            >
                <ArrowSmallIcon />
            </div>
            {props.enabled ? (
                    <Swiper
                        className={styles.swiper}
                        modules={[Mousewheel, Navigation, Grid]}
                        direction={direction}
                        slidesPerView={slidersPerView}
                        grid={{ rows: slidersPerColumn, fill: 'row' }}
                        spaceBetween={spaceBetween}
                        mousewheel={props.mousewheel}
                        navigation={{
                            prevEl,
                            nextEl,
                        }}
                        initialSlide={props.selectedElementIndex}
                        onTransitionStart={props.startSwipeCallBack}
                        onTransitionEnd={props.endSwipeCallBack}
                        allowTouchMove={swipingEnabled}
                        touchReleaseOnEdges={true}
                    >
                        {childrenArray.map((child, index) => {
                            return <SwiperSlide key={index}>{child}</SwiperSlide>;
                        })}
                    </Swiper>
                
            ) : (
                <>{props.children}</>
            )}
            <div
                className={classNames(styles.right_arrow, {
                    [styles.hidden]: !props.enabled,
                    [styles.vertical]: direction === 'vertical',
                    [styles.wws]: props.isWWS,
                })}
                ref={rightArrowRef}
            >
                <ArrowSmallIcon />
            </div>
        </div>
    );
};
